import { useState, useMemo, useRef, useEffect, useCallback, useDeferredValue, Fragment } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

import { createIntFormatter, currencyFormatter } from "../../../utils/formatter/formatter";
import { LoadingContent, TableDownloadButton } from "../../../components/SharedComponents/sharedComponents.index";
import { LOADING_CONTENT_TYPE } from "../../../components/SharedComponents/LoadingContent/loading.content.constants";

import { OtbSelect } from "./OtbDetalhado/components/OtbSelect";

import { 
  PlanoDeCompraRecomendacoes, 
  buildSupplierColumns, 
  buildSupplierRows 
} from './PlanoDeCompraRecomendacoes'

import { SYSTEM_COLORS } from "../../../components/system.colors";

import { OTB_COLORS } from "./otb.colors.constants";

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const MONTH_NAMES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const CATEGORIA_WIDTH = 300;
const PLANO_BASE_WIDTH = 58;
const PLANO_CHAR_WIDTH = 7;
const MONTH_COL_WIDTH = 90;
const CMV_COL_WIDTH = 110;
const VERBA_COL_WIDTH = 140;
const ROW_HEIGHT = 52;
const GROUP_ROW_HEIGHT = 36;
const CATEGORIA_MAX_WIDTH = 480;
const MONTHS_GROUP_MIN_WIDTH = 200;
const MONTH_COL_MAX_WIDTH = 130;

const intFmt = createIntFormatter();

const parseCategoria = (categoria = "") => {
  const parts = categoria.split(" - ");

  return parts.length > 1
    ? {
        productLevel: parts[0].trim(),
        sku: parts.at(-1).trim(),
      }
    : {
        productLevel: "Sem Gênero",
        sku: categoria.trim(),
      };
};

const groupHeaderStyle = (background) => ({
  padding: "10px 10px", fontSize: "11.5px", fontWeight: "700", letterSpacing: 0.5,
  textTransform: "uppercase", textAlign: "center", whiteSpace: "nowrap",
  overflow: "hidden", textOverflow: "ellipsis",
  color: SYSTEM_COLORS.ORANGE.CINNABAR, background,
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.ALTO}`, borderRight: `1px solid ${SYSTEM_COLORS.GRAY.ALTO}`,
  position: "sticky", top: 0, zIndex: 2,
  height: GROUP_ROW_HEIGHT, boxSizing: "border-box",
});

const thBase = (stickyLeft, left, align = "right", background) => ({
  padding: "10px 10px", fontSize: "11px", fontWeight: "700", color: "#1a1a1a",
  textAlign: align, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.ALTO}`, borderRight: `1px solid ${SYSTEM_COLORS.GRAY.CULTURED}`,
  background: background ?? (stickyLeft ? "#eeeeea" : "#f9f9f7"),
  position: "sticky", top: GROUP_ROW_HEIGHT, zIndex: stickyLeft ? 3 : 2,
  ...(stickyLeft ? { left, boxShadow: "2px 0 4px rgba(0,0,0,0.06)" } : {}),
});

const tdBase = (i, sticky, left, align = "right") => ({
  padding: "9px 10px", fontSize: "12.5px", color: SYSTEM_COLORS.BLACK.JET,
  textAlign: align, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.CULTURED}`, borderRight: `1px solid ${SYSTEM_COLORS.GRAY.CULTURED}`,
  backgroundColor: i % 2 === 0 ? SYSTEM_COLORS.WHITE.WHITE : SYSTEM_COLORS.WHITE.WHITE_SMOKE,
  ...(sticky ? { position: "sticky", left, zIndex: 1, boxShadow: "2px 0 4px rgba(0,0,0,0.06)" } : {}),
});

const monthCellStyle = (i, v, maxVal) => {
  const alpha = v > 0 ? 0.06 + (v / maxVal) * 0.55 : 0;
  return {
    ...tdBase(i),
    backgroundColor: v > 0 ? `rgba(232,119,58,${alpha})` : i % 2 === 0 ? SYSTEM_COLORS.WHITE.WHITE : SYSTEM_COLORS.WHITE.WHITE_SMOKE
  };
};

const SummaryStat = ({ ctx }) => {

  const { label, value, caption, valueColor } = ctx || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <span style={{ fontSize: "14px", fontWeight: "600", color: "#1a1a1a" }}>
        {label}
      </span>

      <strong style={{ fontSize: "30px", fontWeight: "700", lineHeight: 1.1, color: valueColor }}>
        {value}
      </strong>

      <span style={{ fontSize: "12px", color: "#777" }}>
        {caption}
      </span>
    </div>
  );
};

const SortIcon = ({ ctx }) => {

  const { direction } = ctx || {};

  if (direction === "asc") return <ArrowUp size={12} color="#e8773a" />;
  if (direction === "desc") return <ArrowDown size={12} color="#e8773a" />;

  return <ArrowUpDown size={12} color="#999" />;
};

export function PlanoDeCompraSection({ actions, ctx, uiState }) {

  const { onExpandProduct } = actions || {};
  const { salesPlanData, detailedData, categoryHierarchy, brands, initialFilter } = ctx || {};
  const { isLoadingOtbSalesPlan, isLoadingDetailed } = uiState || {};

  const [deselectedProdutos, setDeselectedProdutos] = useState([]);
  const [deselectedEstacoes, setDeselectedEstacoes] = useState([]);
  const [selectedMonthKeys, setSelectedMonthKeys] = useState(null);
  const [deselectedGeneros, setDeselectedGeneros] = useState([]);
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [deselectedBrands, setDeselectedBrands] = useState([]);
  const [sort, setSort] = useState(null);

  const initialFilterAppliedRef = useRef(false);

  const toggleRow = (rowKey, row) => {
    const isClosing = expandedRowKey === rowKey;

    setExpandedRowKey(isClosing ? null : rowKey);
    onExpandProduct?.(isClosing ? null : {
      productHierarchyNodeId: row.productHierarchyNodeId,
      parentLevelId: row.parentLevelId,
    });
  };

  const toggleSort = (key) => {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, direction: "desc" };
      if (prev.direction === "desc") return { key, direction: "asc" };
      return null;
    });
  };

  const processedRows = useMemo(() => {
    const groups = {};
    const cmvMap = {};
    const monthlyCostMap = {};
    const supplierMap = {};
    const rowMonthMap = {};

    const now = new Date();
    const nowYear = now.getFullYear();
    const nowMonth = now.getMonth() + 1;

    (salesPlanData || []).forEach(item => {

      const category = item?.categoria || "";
      const otbName = item?.otbName;

      const year = Number(item?.year);
      const month = Number(item?.month);

      const { productLevel, sku } = parseCategoria(category);

      const key = `${otbName}||${category}`;

      const cost = Number(item?.unitCost);
      const unitsSold = Number(item?.unitsSold);

      if (cost > 0) {
        if (!cmvMap[key]) cmvMap[key] = {};
        if (!cmvMap[key][year]) cmvMap[key][year] = [];
        cmvMap[key][year].push(cost);
      }

      if (cost > 0 && unitsSold > 0 && month >= 1 && month <= 12) {
        const costColKey = `${year}-${month}`;

        if (!monthlyCostMap[key]) monthlyCostMap[key] = {};
        if (!monthlyCostMap[key][costColKey]) monthlyCostMap[key][costColKey] = { costSum: 0, salesUnits: 0 };

        monthlyCostMap[key][costColKey].costSum += cost * unitsSold;
        monthlyCostMap[key][costColKey].salesUnits += unitsSold;
      }

      if (!groups[key]) {
        groups[key] = {
          categoria: category,
          productLevel,
          sku,
          otbName,
          filterLevelName: item?.filterLevelName,
          productHierarchyNodeId: item?.productHierarchyNodeId,
          parentLevelId: item?.parentId,
          anos: new Set(),
          valores: {},
          brandNames: new Set(),
          hasColorData: false,
          hasSizeData: false,
          hasPriceData: false,
        };
      }

      groups[key].anos.add(year);

      if (item?.hasColorData) groups[key].hasColorData = true;
      if (item?.hasSizeData) groups[key].hasSizeData = true;
      if (item?.hasPriceData) groups[key].hasPriceData = true;
      if (item?.brandName) groups[key].brandNames.add(item.brandName);

      if (item?.brandName && year && month >= 1 && month <= 12) {
        const supplierKey = `${otbName}||${category}`;
        const colKey = `${year}-${month}`;

        if (!supplierMap[supplierKey]) supplierMap[supplierKey] = {};
        if (!supplierMap[supplierKey][item.brandName]) supplierMap[supplierKey][item.brandName] = {};

        supplierMap[supplierKey][item.brandName][colKey] =
          (supplierMap[supplierKey][item.brandName][colKey] || 0) +
          (Number(item?.inboundInventoryUnits) || 0);
      }

      if (month >= 1 && month <= 12) {
        const colKey = `${year}-${month}`;

        groups[key].valores[colKey] =
          (groups[key].valores[colKey] || 0) +
          Math.abs(Number(item?.grossOpenToBuyUnits));
      }

      if (!item?.brandName && month >= 1 && month <= 12) {
        const colKey = `${year}-${month}`;

        if (!rowMonthMap[key]) rowMonthMap[key] = {};
        if (!rowMonthMap[key][colKey]) {
          rowMonthMap[key][colKey] = { initial: 0, ideal: 0, inbound: 0, plannedSales: null, achievedSales: null };
        }

        const entry = rowMonthMap[key][colKey];

        entry.initial += Number(item?.initialInventoryUnits) || 0;
        entry.ideal += Number(item?.idealInventoryUnits) || 0;
        entry.inbound += Number(item?.inboundInventoryUnits) || 0;

        if (item?.plannedUnitsSold != null) {
          entry.plannedSales = (entry.plannedSales ?? 0) + Number(item.plannedUnitsSold);
        }

        if (item?.achievedUnitsSold != null) {
          entry.achievedSales = (entry.achievedSales ?? 0) + Number(item.achievedUnitsSold);
        }
      }
    });

    const rowPurchaseMap = {};

    Object.entries(rowMonthMap).forEach(([rowKey, byMonth]) => {
      const orderedKeys = Object.keys(byMonth).sort((a, b) => {
        const [yearA, monthA] = a.split("-").map(Number);
        const [yearB, monthB] = b.split("-").map(Number);

        return yearA - yearB || monthA - monthB;
      });

      rowPurchaseMap[rowKey] = {};
      let carriedStock = null;

      orderedKeys.forEach(colKey => {
        const [year, month] = colKey.split("-").map(Number);
        const entry = byMonth[colKey];
        const isCurrentMonth = year === nowYear && month === nowMonth;
        const initial = isCurrentMonth ? entry.initial : carriedStock;
        const sales = entry.plannedSales ?? entry.achievedSales ?? 0;

        if (initial == null) {
          rowPurchaseMap[rowKey][colKey] = 0;
          carriedStock = entry.ideal;
          return;
        }

        const finalStock = initial + entry.inbound - sales;

        rowPurchaseMap[rowKey][colKey] = Math.max(entry.ideal - finalStock, 0);
        carriedStock = Math.max(finalStock, entry.ideal);
      });
    });

    return Object.entries(groups).map(([key, row]) => {
      const anos = Array.from(row.anos).sort((a, b) => a - b);

      const supplierData = Object.entries(supplierMap[`${row.otbName}||${row.categoria}`] || {})
        .map(([brandName, valores]) => ({ brandName, valores }));

      const unitCostByMonth = Object.fromEntries(
        Object.entries(monthlyCostMap[key] || {})
          .map(([colKey, { costSum, salesUnits }]) => [colKey, costSum / salesUnits])
      );

      const valores = Object.fromEntries(
        Object.keys(row.valores).map(colKey => [colKey, rowPurchaseMap[key]?.[colKey] ?? 0])
      );

      return {
        ...row,
        valores,
        anos,
        brandNames: Array.from(row.brandNames),
        otbLabel: row.otbName,
        costsByYear: cmvMap[key] || {},
        unitCostByMonth,
        supplierData
      };
    });

  }, [salesPlanData]);

  useEffect(() => {
    if (!initialFilter || initialFilterAppliedRef.current || processedRows.length === 0) return;

    let monthColKey = null;

    if (initialFilter.monthKey) {
      const [year, month] = initialFilter.monthKey.split("-").map(Number);
      monthColKey = `${year}-${month}`;
    }

    const hasPurchase = (row) => Math.round(row.valores[monthColKey] || 0) > 0;

    const candidateRows = initialFilter.onlyWithPurchase
      ? processedRows.filter(hasPurchase)
      : processedRows.filter(row => row.categoria === initialFilter.category);

    if (candidateRows.length === 0) return;

    initialFilterAppliedRef.current = true;

    if (monthColKey) setSelectedMonthKeys([monthColKey]);

    const otbMatchedRows = candidateRows.filter(row => row.otbLabel === initialFilter.otbName);
    const targetRows = otbMatchedRows.length > 0 ? otbMatchedRows : candidateRows;

    const targetOtbs = new Set(targetRows.map(row => row.otbLabel));
    const targetCategories = new Set(targetRows.map(row => row.categoria));
    const targetGenres = new Set(targetRows.map(row => row.filterLevelName));

    const allOtbs = [...new Set(processedRows.map(row => row.otbLabel))];
    const allCategories = [...new Set(processedRows.map(row => row.categoria))];
    const allGenres = [...new Set(processedRows.map(row => row.filterLevelName).filter(Boolean))];

    setDeselectedEstacoes(allOtbs.filter(otb => !targetOtbs.has(otb)));
    setDeselectedProdutos(allCategories.filter(category => !targetCategories.has(category)));
    setDeselectedGeneros(allGenres.filter(genre => !targetGenres.has(genre)));
  }, [initialFilter, processedRows]);

  const otbColors = useMemo(() => {
    const labels = [...new Set(processedRows.map(row => row.otbLabel))]
      .sort((a, b) => a.localeCompare(b));

    return Object.fromEntries(labels.map((label, index) => [label, OTB_COLORS[index % OTB_COLORS.length]]));
  }, [processedRows]);

  const planoColWidth = useMemo(() => {
    const longestLabel = Math.max(...Object.keys(otbColors).map(label => String(label).length), 5);
    return PLANO_BASE_WIDTH + longestLabel * PLANO_CHAR_WIDTH;
  }, [otbColors]);

  const brandCategoryMap = useMemo(() => {
    const map = new Map();

    (brands || []).forEach(({ brandName, categoria }) => {
      if (!brandName) return;
      if (!map.has(brandName)) map.set(brandName, new Set());
      map.get(brandName).add(categoria);
    });

    return map;
  }, [brands]);

  const brandOptions = useMemo(() => {
    const names = brandCategoryMap.size > 0
      ? [...brandCategoryMap.keys()]
      : processedRows.flatMap(row => row.brandNames);

    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [brandCategoryMap, processedRows]);

  const selectedBrands = useMemo(
    () => brandOptions.filter(opt => !deselectedBrands.includes(opt)),
    [brandOptions, deselectedBrands]
  );

  const selectedBrandsSet = useMemo(() => new Set(selectedBrands), [selectedBrands]);
  const hasBrandFilter = selectedBrands.length < brandOptions.length;
  const hasSupplierSelected = brandOptions.length === 0 || selectedBrands.length > 0;

  const onChangeBrands = (value) => {
    setDeselectedBrands(prev => [
      ...prev.filter(opt => !brandOptions.includes(opt)),
      ...brandOptions.filter(opt => !value.includes(opt)),
    ]);
  };

  const rowsByBrand = useMemo(() => {
    if (!hasBrandFilter) return processedRows;

    if (brandCategoryMap.size === 0) {
      return processedRows.filter(row => row.brandNames.some(name => selectedBrandsSet.has(name)));
    }

    const allowedCategories = new Set();

    selectedBrands.forEach(name => {
      brandCategoryMap.get(name)?.forEach(categoria => allowedCategories.add(categoria));
    });

    return processedRows.filter(row => allowedCategories.has(row.categoria));
  }, [processedRows, selectedBrands, selectedBrandsSet, hasBrandFilter, brandCategoryMap]);

  const availableOtbs = useMemo(
    () => [...new Set(rowsByBrand.map(row => row.otbLabel))].sort((a, b) => a.localeCompare(b)),
    [rowsByBrand]
  );

  const selectedEstacoes = useMemo(
    () => availableOtbs.filter(otb => !deselectedEstacoes.includes(otb)),
    [availableOtbs, deselectedEstacoes]
  );

  const onChangeEstacoes = (value) => {
    setDeselectedEstacoes(prev => [
      ...prev.filter(otb => !availableOtbs.includes(otb)),
      ...availableOtbs.filter(otb => !value.includes(otb)),
    ]);
  };

  const rowsByOtb = useMemo(() => {
    const otbSet = new Set(selectedEstacoes);
    return rowsByBrand.filter(row => otbSet.has(row.otbLabel));
  }, [rowsByBrand, selectedEstacoes]);

  const filterOptions = useMemo(() => {
    const names = rowsByOtb
      .filter(row => row.filterLevelName)
      .map(row => row.filterLevelName);

    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [rowsByOtb]);

  const selectedGeneros = useMemo(
    () => filterOptions.filter(opt => !deselectedGeneros.includes(opt)),
    [filterOptions, deselectedGeneros]
  );

  const onChangeGeneros = (value) => {
    setDeselectedGeneros(prev => [
      ...prev.filter(opt => !filterOptions.includes(opt)),
      ...filterOptions.filter(opt => !value.includes(opt)),
    ]);
  };

  const rowsByGenero = useMemo(() => {
    const deselectedSet = new Set(deselectedGeneros);
    return rowsByOtb.filter(row => !deselectedSet.has(row.filterLevelName));
  }, [rowsByOtb, deselectedGeneros]);

  const allAvailableProducts = useMemo(
    () => [...new Set(rowsByGenero.map(row => row.categoria))],
    [rowsByGenero]
  );

  const selectedProdutos = useMemo(
    () => allAvailableProducts.filter(produto => !deselectedProdutos.includes(produto)),
    [allAvailableProducts, deselectedProdutos]
  );

  const selectedProdutosSet = useMemo(() => new Set(selectedProdutos), [selectedProdutos]);

  const categoryTree = useMemo(() => {
    const availableLabels = new Set(allAvailableProducts);
    return {
      levels: categoryHierarchy?.levels ?? [],
      categories: (categoryHierarchy?.categories ?? []).filter((category) => availableLabels.has(category.categoria)),
    };
  }, [categoryHierarchy, allAvailableProducts]);

  const onChangeProdutos = (next) => {
    const treeProdutos = categoryTree.categories.map(category => category.categoria);

    setDeselectedProdutos(prev => [
      ...prev.filter(produto => !treeProdutos.includes(produto)),
      ...treeProdutos.filter(produto => !next.includes(produto)),
    ]);

    if (next.length === 0) return;

    const nextSet = new Set(next);
    const generosWithSelection = new Set(
      rowsByOtb
        .filter(row => nextSet.has(row.categoria))
        .map(row => row.filterLevelName)
    );

    setDeselectedGeneros(prev => [
      ...prev.filter(genero => !filterOptions.includes(genero)),
      ...filterOptions.filter(genero => !generosWithSelection.has(genero)),
    ]);
  };

  const categoryDisplayLevelIndexes = useMemo(() => {
    const total = categoryTree.levels.length;
    return total > 1 ? [0, total - 1] : [0];
  }, [categoryTree.levels]);

  const rowsByProduct = useMemo(
    () => rowsByGenero.filter(row => selectedProdutosSet.has(row.categoria)),
    [rowsByGenero, selectedProdutosSet]
  );

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentColKey = `${currentYear}-${currentMonth}`;

  const availableMonths = useMemo(() => {
    const keys = new Set();

    rowsByProduct.forEach(row => {
      Object.keys(row.valores).forEach(colKey => {
        const [ano, mes] = colKey.split("-").map(Number);

        if (ano > currentYear || (ano === currentYear && mes >= currentMonth)) {
          keys.add(colKey);
        }
      });
    });

    return Array.from(keys)
      .map(colKey => {
        const [ano, mes] = colKey.split("-").map(Number);
        return { key: colKey, ano, mes };
      })
      .sort((a, b) => a.ano - b.ano || a.mes - b.mes);
  }, [rowsByProduct, currentYear, currentMonth]);

  const selectedMonths = useMemo(() => {
    if (selectedMonthKeys) {
      const selectedSet = new Set(selectedMonthKeys);
      return availableMonths.filter(month => selectedSet.has(month.key)).map(month => month.key);
    }

    const hasCurrentYear = availableMonths.some(month => month.ano === currentYear);

    return availableMonths
      .filter(month => !hasCurrentYear || month.ano === currentYear)
      .map(month => month.key);
  }, [selectedMonthKeys, availableMonths, currentYear]);

  const selectedMonthsSet = useMemo(() => new Set(selectedMonths), [selectedMonths]);

  const anosSelecionados = useMemo(
    () => [...new Set(selectedMonths.map(colKey => Number(colKey.split("-")[0])))],
    [selectedMonths]
  );

  const periodTree = useMemo(() => ({
    levels: [{ levelName: "Ano" }, { levelName: "Mês" }],
    categories: availableMonths.map(({ key, ano, mes }) => ({
      categoria: key,
      path: [String(ano), MONTH_NAMES[mes - 1]],
    })),
  }), [availableMonths]);

  const filteredRows = useMemo(
    () => rowsByProduct.filter(row => selectedMonths.some(colKey => colKey in row.valores)),
    [rowsByProduct, selectedMonths]
  );

  const supplierColumns = useMemo(buildSupplierColumns, []);

  const columns = useMemo(() => selectedMonths.map(colKey => {
    const [ano, mes] = colKey.split("-").map(Number);

    return {
      key: colKey,
      ano,
      mes,
      label: `${MONTHS[mes - 1].toUpperCase()}/${String(ano).slice(-2)}`
    };
  }), [selectedMonths]);

  const displayRows = useMemo(() => {
    return filteredRows.map(row => {
      const selectedEntries = Object.entries(row.valores)
        .filter(([colKey]) => selectedMonthsSet.has(colKey));

      const acumulado = selectedEntries.reduce((sum, [, value]) => sum + value, 0);

      const costs = anosSelecionados.flatMap(ano => row.costsByYear[ano] || []);
      const simpleCmv = costs.length
        ? costs.reduce((sum, value) => sum + value, 0) / costs.length
        : 0;

      const verba = selectedEntries
        .reduce((sum, [colKey, value]) => sum + value * (row.unitCostByMonth[colKey] ?? simpleCmv), 0);

      const cmv = acumulado > 0 ? verba / acumulado : simpleCmv;

      return { ...row, acumulado, cmv, verba };
    });
  }, [filteredRows, selectedMonthsSet, anosSelecionados]);

  const visibleRows = useDeferredValue(displayRows);

  const sortedRows = useMemo(() => {
    if (!sort) return visibleRows;

    const getValue = (row) => sort.key === "verba" ? row.verba : row.valores[sort.key] || 0;
    const factor = sort.direction === "asc" ? 1 : -1;

    return [...visibleRows].sort((a, b) => (getValue(a) - getValue(b)) * factor);
  }, [visibleRows, sort]);

  const totals = useMemo(() => {
    const valores = {};

    visibleRows.forEach(row => {
      columns.forEach(column => {
        valores[column.key] =
          (valores[column.key] || 0) +
          (row.valores[column.key] || 0);
      });
    });

    return {
      valores,
      acumulado: Object.values(valores)
        .reduce((sum, value) => sum + value, 0),
      verba: visibleRows
        .reduce((sum, row) => sum + row.verba, 0),
    };

  }, [visibleRows, columns]);

  const maxMonthValue = useMemo(() => {
    let max = 0;

    visibleRows.forEach(row => {
      columns.forEach(column => {
        const value = row.valores[column.key] || 0;

        if (value > max) {
          max = value;
        }
      });
    });

    return max || 1;

  }, [visibleRows, columns]);

  const getExportRows = () => [
    ["Categoria", "OTB", ...columns.map(column => column.label), "Acumulado", "CMV", "Verba"],
    ...visibleRows.map(row => [
      `${row.sku} - ${row.productLevel}`,
      row.otbLabel,
      ...columns.map(column => row.valores[column.key] || 0),
      row.acumulado,
      row.cmv || null,
      row.verba || null,
    ]),
    ["TOTAL", "", ...columns.map(column => totals.valores[column.key] || 0), totals.acumulado, null, totals.verba],
  ];

  const scrollRef = useRef(null);

  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const element = scrollRef.current;

    if (!element) return undefined;

    const observer = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
    observer.observe(element);

    return () => observer.disconnect();
  }, [isLoadingOtbSalesPlan]);

  const rowVirtualizer = useVirtualizer({
    count: sortedRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
    measureElement: (element) => {
      const detail = element.nextElementSibling;
      const detailHeight = detail?.dataset.detail ? detail.getBoundingClientRect().height : 0;

      return element.getBoundingClientRect().height + detailHeight;
    },
  });

  const detailObserverRef = useRef(null);

  const observeDetailRow = useCallback((element) => {
    detailObserverRef.current?.disconnect();
    detailObserverRef.current = null;

    if (!element) return;

    const observer = new ResizeObserver(() => {
      const mainRow = element.previousElementSibling;
      if (mainRow) rowVirtualizer.measureElement(mainRow);
    });

    observer.observe(element);
    detailObserverRef.current = observer;
  }, [rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length ? virtualRows[0].start : 0;
  const paddingBottom = virtualRows.length
    ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
    : 0;

  const totalColSpan = columns.length + 4;

  const fixedColsWidth = planoColWidth + CMV_COL_WIDTH + VERBA_COL_WIDTH;
  const monthColMinWidth = Math.max(MONTH_COL_WIDTH, Math.ceil(MONTHS_GROUP_MIN_WIDTH / Math.max(columns.length, 1)));
  const monthColMaxWidth = Math.max(MONTH_COL_MAX_WIDTH, monthColMinWidth);
  const monthColWidth = columns.length > 0
    ? Math.floor(Math.min(
        Math.max((containerWidth - fixedColsWidth - CATEGORIA_WIDTH) / columns.length, monthColMinWidth),
        monthColMaxWidth
      ))
    : monthColMinWidth;
  const monthsWidth = columns.length * monthColWidth;
  const categoriaColWidth = Math.floor(Math.min(
    Math.max(containerWidth - fixedColsWidth - monthsWidth, CATEGORIA_WIDTH),
    CATEGORIA_MAX_WIDTH
  ));
  const tableWidth = categoriaColWidth + fixedColsWidth + monthsWidth;

  const renderSortableLabel = (key, label) => (
    <button
      type="button"
      onClick={() => toggleSort(key)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        border: "none", background: "transparent", padding: 0,
        font: "inherit", cursor: "pointer",
        color: sort?.key === key ? SYSTEM_COLORS.ORANGE.CINNABAR : "inherit",
      }}
    >
      {label}
      <SortIcon ctx={{ direction: sort?.key === key ? sort.direction : null }} />
    </button>
  );

  if (isLoadingOtbSalesPlan) {
    return (
      <LoadingContent
        label="Carregando dados..."
        type={LOADING_CONTENT_TYPE.API_PAGE_DATA}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.6rem", height: "100%" }}>

      <section style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ inlineSize: 4, blockSize: 22, borderRadius: 2, background: "#e8773a", marginTop: 4 }}/>

          <hgroup style={{ inlineSize: "fit-content" }}>
            <h1 style={{ fontSize: "22px", fontWeight: "700", color: "#1a1a1a", margin: 0 }} >
              Plano de Compra
            </h1>

            <p style={{ fontSize: "13px", color: "#999", margin: "4px 0 0" }}>
              Projeção mensal de compra por categoria
            </p>
          </hgroup>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {brandOptions.length > 0 && (
            <OtbSelect
              label={"FORNECEDORES"}
              icon={"truck"}
              searchable
              searchPlaceholder="Buscar fornecedores..."
              options={brandOptions}
              value={selectedBrands}
              onChange={onChangeBrands}
              notice="Este filtro limita os demais dados da tabela."
            />
          )}

          <OtbSelect
            label={"PLANO"}
            options={availableOtbs}
            value={selectedEstacoes}
            onChange={onChangeEstacoes}
            disabled={!hasSupplierSelected}
          />

          <OtbSelect
            label={"DEPARTAMENTO"}
            options={filterOptions}
            value={selectedGeneros}
            onChange={onChangeGeneros}
            disabled={!hasSupplierSelected}
          />

          <OtbSelect
            label={"CATEGORIA"}
            title={"PRODUTO"}
            tree={categoryTree}
            treeMultiple
            value={selectedProdutos}
            displayLevelIndexes={categoryDisplayLevelIndexes}
            onChange={onChangeProdutos}
            notice={hasBrandFilter ? "Lista restrita aos fornecedores selecionados." : null}
            disabled={!hasSupplierSelected}
          />
        </div>
      </section>

      <section style={{ 
          background: SYSTEM_COLORS.WHITE.WHITE, borderRadius: "12px", border: `1px solid ${SYSTEM_COLORS.GRAY.ALTO}`, padding: "18px 24px",
          display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }} >

        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 48, flexWrap: "wrap" }}>
          <SummaryStat
            ctx={{
              label: "Necessidade de Compra",
              value: intFmt.format(totals.acumulado),
              caption: "peças",
              valueColor: "#e8773a"
            }}
          />

          <div style={{ inlineSize: 1, blockSize: 52, background: "#e8e8e8" }} />

          <SummaryStat
            ctx={{
              label: "Verba para Compra",
              value: currencyFormatter.format(totals.verba),
              caption: `${visibleRows.length} categorias`,
              valueColor: "#1a1a1a"
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <OtbSelect
            label={"PERÍODO"}
            title={"Ano → Meses"}
            icon={"calendar"}
            periodMode
            tree={periodTree}
            treeMultiple
            value={selectedMonths}
            onChange={setSelectedMonthKeys}
          />

          <TableDownloadButton
            ctx={{
              tableId: "plano-compra-table",
              fileName: "plano_de_compra",
              sheetName: "Plano de Compra",
              hideOnExport: ["recomendacoes"],
              getExportRows
            }}
            uiState={{
              useCustomIcon: true,
              iconColor: "#e8773a",
              borderColor: "#e8773a"
            }}
          />
        </div>
      </section>

      <section style={{ flex: 1, minBlockSize: 0, display: "flex", alignItems: "flex-start" }}>
        <section
          ref={scrollRef}
          style={{
            background: SYSTEM_COLORS.WHITE.WHITE,
            borderRadius: "12px",
            border: `1px solid ${SYSTEM_COLORS.GRAY.ALTO}`,
            maxBlockSize: "100%",
            inlineSize: "100%",
            overflow: "auto"
          }}
        >
          <table
            id="plano-compra-table"
            style={{
              borderCollapse: "separate",
              borderSpacing: 0,
              tableLayout: "fixed",
              inlineSize: tableWidth
            }}>
            <colgroup>
              <col style={{ width: categoriaColWidth }} />
              <col style={{ width: planoColWidth }} />

              {columns.map(column => (
                <col key={column.key} style={{ width: monthColWidth }} />
              ))}

              <col style={{ width: CMV_COL_WIDTH }} />
              <col style={{ width: VERBA_COL_WIDTH }} />
            </colgroup>

            <thead>
              <tr>
                <th colSpan={2} style={groupHeaderStyle("#eeeeea")}>
                  Necessidade de Compra
                </th>

                <th colSpan={columns.length} style={groupHeaderStyle("#f9f9f7")}>
                  Programação da Compra
                </th>

                <th colSpan={2} style={groupHeaderStyle("#f3e7e1")}>
                  Verba para Compra
                </th>
              </tr>

              <tr>
                <th style={thBase(true, 0, "left")}>
                  CATEGORIA
                </th>

                <th style={thBase(false, 0, "center")}>
                  PLANO
                </th>

                {columns.map(column => (
                  <th
                    key={column.key}
                    style={thBase(false, 0)}
                  >
                    {renderSortableLabel(column.key, column.label)}
                  </th>
                ))}

                <th style={thBase(false, 0, "right", "#f3e7e1")}>
                  CMV
                </th>

                <th style={thBase(false, 0, "right", "#f3e7e1")}>
                  {renderSortableLabel("verba", "VERBA")}
                </th>
              </tr>
            </thead>

            <tbody>
              {paddingTop > 0 && (
                <tr style={{ height: paddingTop }}>
                  <td colSpan={totalColSpan} style={{ padding: 0, border: "none" }} />
                </tr>
              )}

              {virtualRows.map((virtualRow) => {
                const row = sortedRows[virtualRow.index];
                const i = virtualRow.index;
                const rowKey = `${row.otbName}-${row.categoria}-${row.anos.join("-")}`;
                const isExpanded = expandedRowKey === rowKey;
                const canExpand = row.hasColorData
                  || row.hasSizeData
                  || row.hasPriceData
                  || buildSupplierRows(row.supplierData, supplierColumns).length > 0;

                return (
                  <Fragment key={rowKey}>
                    <tr
                      className="plano-row"
                      data-index={virtualRow.index}
                      ref={rowVirtualizer.measureElement}
                    >
                      <td
                        onClick={canExpand ? () => toggleRow(rowKey, row) : undefined}
                        style={{
                          ...tdBase(i, true, 0, "left"),
                          cursor: canExpand ? "pointer" : "default",
                          ...(isExpanded ? { boxShadow: "inset 3px 0 0 #e8773a, 2px 0 4px rgba(0,0,0,0.06)" } : {}),
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {canExpand ? (
                            <ChevronDown
                              size={14}
                              color="#e8773a"
                              style={{ flexShrink: 0, transform: isExpanded ? "none" : "rotate(-90deg)", transition: "transform 0.2s" }}
                            />
                          ) : (
                            <span style={{ inlineSize: 14, flexShrink: 0 }} />
                          )}

                          <div style={{ minWidth: 0 }} title={row.categoria}>
                            <div
                              style={{
                                fontWeight: "600",
                                fontSize: "12.5px",
                                color: "#1a1a1a",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }}
                            >
                              {row.sku}
                            </div>

                            <div
                              style={{
                                fontSize: "11px",
                                color: "#999",
                                marginTop: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }}
                            >
                              {row.categoria.split(" - ").slice(0, -1).join(" - ") || row.productLevel}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td style={tdBase(i, false, 0, "center")}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "3px 10px",
                            borderRadius: "20px",
                            backgroundColor: `${otbColors[row.otbLabel]}26`,
                            color: SYSTEM_COLORS.BLACK.JET,
                            fontSize: "11.5px",
                            fontWeight: "600",
                            whiteSpace: "nowrap"
                          }}
                        >
                          <span
                            style={{
                              inlineSize: 8,
                              blockSize: 8,
                              borderRadius: "50%",
                              flexShrink: 0,
                              background: otbColors[row.otbLabel]
                            }}
                          />
                          {row.otbLabel}
                        </span>
                      </td>

                      {columns.map(column => {
                        const value = Math.round(row.valores[column.key] || 0);
                        const isCurrentMonth = column.key === currentColKey;

                        return (
                          <td
                            key={column.key}
                            style={monthCellStyle(
                              i,
                              value,
                              maxMonthValue
                            )}
                          >
                            <span
                              style={{
                                color: !value ? "#ccc" : isCurrentMonth ? "#e8773a" : SYSTEM_COLORS.BLACK.JET,
                                fontWeight: value && isCurrentMonth ? "700" : "400"
                              }}
                            >
                              {value > 0 ? intFmt.format(value) : "-"}
                            </span>
                          </td>
                        );
                      })}

                      <td
                        style={{
                          ...tdBase(i),
                          color: "#555",
                        }}
                      >
                        {row.cmv
                          ? currencyFormatter.format(row.cmv)
                          : "-"}
                      </td>

                      <td
                        style={{
                          ...tdBase(i),
                          color: "#1a1a1a",
                        }}
                      >
                        {row.verba
                          ? currencyFormatter.format(row.verba)
                          : "-"}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="hide-on-export" data-detail="true" ref={observeDetailRow}>
                        <td colSpan={totalColSpan} style={{ 
                          padding: 0, 
                          background: SYSTEM_COLORS.WHITE.OFF_WHITE, 
                          borderBottom: `2px solid ${SYSTEM_COLORS.ORANGE.BURNT_ORANGE_40}` }}>
                          <div style={{ 
                            position: "sticky", left: 0, width: "fit-content", 
                            padding: "16px 20px", boxSizing: "border-box" }}>
                            <PlanoDeCompraRecomendacoes 
                            ctx={{ detailedData, supplierData: row.supplierData }} 
                            uiState={{ isLoading: isLoadingDetailed }} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}

              {paddingBottom > 0 && (
                <tr style={{ height: paddingBottom }}>
                  <td colSpan={totalColSpan} style={{ padding: 0, border: "none" }} />
                </tr>
              )}
            </tbody>

            <tfoot>
              <tr
                style={{
                  borderTop: "2px solid #e0e0e0"
                }}
              >
                <td
                  style={{
                    ...tdBase(-1, true, 0, "left"),
                    fontWeight: "700",
                    color: "#1a1a1a",
                    background: "#f9f9f7"
                  }}
                >
                  TOTAL
                </td>

                <td
                  style={{
                    ...tdBase(-1, false, 0, "center"),
                    background: "#f9f9f7"
                  }}
                />

                {columns.map(column => (
                  <td
                    key={column.key}
                    style={{
                      ...tdBase(-1, false),
                      fontWeight: "700",
                      color: "#1a1a1a",
                      background: "#f9f9f7"
                    }}
                  >
                    {intFmt.format(
                      totals.valores[column.key] || 0
                    )}
                  </td>
                ))}

                <td
                  style={{
                    ...tdBase(-1, false),
                    color: "#777",
                    background: "#f9f9f7"
                  }}
                >
                  -
                </td>

                <td style={{ ...tdBase(-1, false), fontWeight: "700", color: "#1a1a1a", background: "#f9f9f7" }}>
                  {currencyFormatter.format(totals.verba)}
                </td>
              </tr>
            </tfoot>
          </table>
        </section>
      </section>
      
    </div>
  );
};